import React from 'react';

/**
 * TimeSlotList Component - Shows available time slots for a selected date
 * Styled similar to Cal.com's time slot selection
 *
 * @param {Object} props
 * @param {Array} props.slots - Array of time slot objects with start, end, start_time, end_time
 * @param {Date} props.selectedDate - The date for these slots
 * @param {String} props.selectedSlot - Currently selected slot (in format "HH:MM")
 * @param {Function} props.onSlotSelect - Callback when a slot is selected
 * @param {Number} props.duration - Duration in minutes
 * @param {Boolean} props.loading - Loading state
 */
const TimeSlotList = ({
  slots = [],
  selectedDate,
  selectedSlot,
  onSlotSelect,
  duration,
  loading = false,
  translations = {}
}) => {
  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';

    const locale = document.documentElement.lang || 'zh-TW';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  // Group slots by time period (morning, afternoon, evening)
  const groupSlotsByPeriod = (slots) => {
    const periods = {
      morning: [],    // 06:00 - 12:00
      afternoon: [],  // 12:00 - 18:00
      evening: []     // 18:00 - 24:00
    };

    slots.forEach(slot => {
      const hour = parseInt(slot.start.split(':')[0]);

      if (hour >= 6 && hour < 12) {
        periods.morning.push(slot);
      } else if (hour >= 12 && hour < 18) {
        periods.afternoon.push(slot);
      } else if (hour >= 18 || hour < 6) {
        periods.evening.push(slot);
      }
    });

    return periods;
  };

  const groupedSlots = slots.length > 0 ? groupSlotsByPeriod(slots) : null;

  const periodNames = {
    morning: translations.morning || 'Morning',
    afternoon: translations.afternoon || 'Afternoon',
    evening: translations.evening || 'Evening'
  };

  const periodIcons = {
    morning: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    afternoon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    evening: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    )
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // No date selected
  if (!selectedDate) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">{translations.select_date || 'Select Date'}</h3>
          <p className="mt-2 text-sm text-gray-500">
            {translations.select_date_hint || 'Select a date with available slots from the calendar on the left'}
          </p>
        </div>
      </div>
    );
  }

  // No slots available for selected date
  if (slots.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {formatDate(selectedDate)}
        </h3>
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">{translations.no_slots || 'No available time slots'}</h3>
          <p className="mt-2 text-sm text-gray-500">
            {translations.no_slots_hint || 'No bookable slots for this date. Please select another date.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {formatDate(selectedDate)}
        </h3>
      </div>

      {/* Time Slots by Period */}
      <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
        {Object.entries(groupedSlots).map(([period, periodSlots]) => {
          if (periodSlots.length === 0) return null;

          return (
            <div key={period}>
              {/* Period Header */}
              <div className="flex items-center gap-2 mb-3">
                <div className="text-gray-600">
                  {periodIcons[period]}
                </div>
                <h4 className="text-sm font-medium text-gray-700">
                  {periodNames[period]}
                </h4>
                <div className="flex-1 border-t border-gray-200 ml-2"></div>
              </div>

              {/* Slots Grid */}
              <div className="grid grid-cols-2 gap-2">
                {periodSlots.map((slot, index) => {
                  const isSelected = selectedSlot === slot.start;

                  return (
                    <button
                      key={index}
                      onClick={() => onSlotSelect(slot)}
                      className={`
                        px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all
                        ${isSelected
                          ? 'border-[#3366FF] bg-[#3366FF] text-white'
                          : 'border-gray-200 bg-white text-gray-900 hover:border-[#3366FF] hover:bg-blue-50'
                        }
                      `}
                    >
                      {slot.start}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Note */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          {translations.timezone_hint || 'Times are shown in your local timezone'}
        </p>
      </div>
    </div>
  );
};

export default TimeSlotList;
